import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Card, Chip, CoinBadge, Input, Skeleton, Tabs, baht, fmtCoins } from "../components/ui";
import CreditPacks from "../components/coins/CreditPacks";
import { coinCalc } from "../services/fb/pricing";
import { BannerChars } from "../components/lux";
import ChatIcon from "../components/chat/ChatIcon";
import CoinHistory from "../components/coins/CoinHistory";
import PaymentQrModal from "../components/coins/PaymentQrModal";
import PendingTopups from "../components/coins/PendingTopups";
import { useAsync } from "../hooks";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import api from "../services/api";
import { UNAVAILABLE } from "../components/chat/format";
import "../styles/chat.css";
import "../styles/coins.css";

export default function Coins() {
  const { coins, setCoins, refresh } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "credits" ? "credits" : "coins";
  const pkgs = useAsync(() => api.coins.packages(), []);
  const pricing = useAsync(() => api.coins.pricing(), []);
  const [selId, setSelId] = useState(null);
  const [custom, setCustom] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [version, setVersion] = useState(0); // เปลี่ยนเมื่อเติมสำเร็จ/ปิด QR -> โหลดประวัติ+รายการค้างใหม่

  // ไม่แสดงแพ็กที่ต่ำกว่ายอดเติมขั้นต่ำ (กันแพ็กเก่าหลุดมา) ; ฝั่ง server ก็ปฏิเสธซ้ำ
  const minBaht = pricing.data?.coin.min ?? 300;
  const list = (pkgs.data || []).filter((p) => p.price >= minBaht);
  const customCalc = custom === "" || !pricing.data ? null : coinCalc(Number(custom), pricing.data.coin);
  const customPkg = customCalc && customCalc.ok
    ? { id: "custom", name: `กำหนดเอง ${baht(customCalc.baht)}`, price: customCalc.baht, tokens: customCalc.coins, clubPoint: customCalc.vipPoints }
    : null;
  const sel = selId === "custom" ? customPkg : list.find((p) => p.id === selId) || null;

  // เลือกแพ็กที่ "แนะนำ" ไว้ให้ก่อน (ลดขั้นตอน) ไม่มีก็แพ็กถูกสุด (ไม่ต่ำกว่าขั้นต่ำ)
  useEffect(() => {
    if (list.length && !selId) setSelId((list.find((p) => p.tag === "แนะนำ") || [...list].sort((a, b) => a.price - b.price)[0]).id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkgs.data, pricing.data]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (version > 0) pricing.reload(); }, [version]);

  const applyResult = (r) => {
    if (typeof r?.tokensLeft === "number") setCoins(r.tokensLeft);
    else refresh().catch(() => {});
    setVersion((v) => v + 1);
  };
  const onConfirm = async (qr) => {
    const r = await api.coins.confirm(payFor.id, qr.transactionId);
    applyResult(r);
    toast("เติมคอยน์สำเร็จ", "success");
    return r;
  };

  return (
    <div className="coins-page">
      <div className="page-head page-head--art">
        <div className="grow"><h1>เติมคอยน์</h1><p>คอยน์ใช้คุยกับตัวละคร เริ่มต้น 2 คอยน์ต่อข้อความ · เติมขั้นต่ำ {fmtCoins(minBaht)} บาท ยิ่งเติมมากยิ่งได้โบนัสเพิ่ม</p></div>
        <Card className="row coins-balance" aria-label={`คอยน์คงเหลือ ${coins}`}><span className="muted">คงเหลือ</span><CoinBadge amount={coins} /></Card>
        <BannerChars ids={["07", "394", "124"]} />
      </div>

      <div className="mb-16"><Tabs tabs={[{ value: "coins", label: "เติมคอยน์" }, { value: "credits", label: "แพ็กสร้างตัวละคร" }]} value={tab} onChange={(v) => setParams(v === "credits" ? { tab: "credits" } : {}, { replace: true })} /></div>

      {tab === "credits" ? (
        pricing.loading && !pricing.data ? <Skeleton height={200} radius="16px" />
          : !pricing.data ? <div className="notice notice--error" role="alert"><span className="grow">{pricing.error || "โหลดราคาไม่สำเร็จ"}</span><Button size="sm" onClick={pricing.reload}>ลองใหม่</Button></div>
          : pricing.error ? <div className="notice notice--error" role="alert"><span className="grow">{pricing.error}</span><Button size="sm" onClick={pricing.reload}>ลองใหม่</Button></div>
          : <CreditPacks cfg={pricing.data.create} credits={pricing.data.createCredits} onDone={() => setVersion((v) => v + 1)} onClose={() => setVersion((v) => v + 1)} />
      ) : (<>
      {pkgs.loading ? (
        <div className="pkg-grid">{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={92} radius="16px" />)}</div>
      ) : pkgs.error ? (
        <div className="notice notice--error" role="alert"><span className="grow">{pkgs.error}</span><Button size="sm" onClick={pkgs.reload}>ลองใหม่</Button></div>
      ) : (
        <div className="pkg-grid" role="radiogroup" aria-label="เลือกแพ็กเกจคอยน์">
          {list.map((p) => (
            <button key={p.id} role="radio" aria-checked={selId === p.id} className={`pkg pkg--card${selId === p.id ? " is-active" : ""}`} onClick={() => setSelId(p.id)}>
              <span className="pkg__emoji" aria-hidden="true">{p.emoji}</span>
              <span className="grow">
                <span className="strong">{p.name}</span>
                {p.tag ? <Chip size="sm" className="is-active" style={{ marginLeft: 6 }}>{p.tag}</Chip> : null}
                <span className="muted pkg__meta">
                  {p.bonusCoins ? <>ฐาน {fmtCoins(p.baseCoins)} + โบนัส {fmtCoins(p.bonusCoins)} <b className="pkg__bonus">+{p.bonusPercent}%</b></> : "ไม่มีโบนัส"}
                </span>
                <span className="faint pkg__meta">≈ {fmtCoins(p.maxMessages || Math.floor(p.tokens / 2))} ข้อความ · {fmtCoins(p.clubPoint)} คุยพ้อยท์</span>
              </span>
              <span className="pkg__price"><CoinBadge amount={p.tokens} /><span className="strong">{baht(p.price)}</span></span>
            </button>
          ))}
          {pricing.data ? (
            <div role="radio" aria-checked={selId === "custom"} tabIndex={-1} className={`pkg pkg--card${selId === "custom" ? " is-active" : ""}`} onClick={() => setSelId("custom")} style={{ alignItems: "flex-start" }}>
              <span className="pkg__emoji" aria-hidden="true">✏️</span>
              <span className="grow">
                <span className="strong">กำหนดเอง</span>
                <Input aria-label="ยอดเติมที่ต้องการ (บาท)" type="number" inputMode="numeric" min={pricing.data.coin.min} max={pricing.data.coin.max} step={1}
                  placeholder={`${fmtCoins(pricing.data.coin.min)}–${fmtCoins(pricing.data.coin.max)} บาท`} value={custom}
                  onFocus={() => setSelId("custom")} onChange={(e) => { setSelId("custom"); setCustom(e.target.value); }}
                  error={selId === "custom" && customCalc && !customCalc.ok ? customCalc.reason : undefined} style={{ marginBottom: 0 }} />
                {customCalc && customCalc.ok ? (
                  <span className="muted pkg__meta">ฐาน {fmtCoins(customCalc.base)} + โบนัส {fmtCoins(customCalc.bonus)} <b className="pkg__bonus">+{customCalc.bonusPercent}%</b></span>
                ) : <span className="faint pkg__meta">คอยน์ = บาท x {pricing.data.coin.perBaht} + โบนัสตามยอด</span>}
              </span>
              <span className="pkg__price">{customCalc && customCalc.ok ? <><CoinBadge amount={customCalc.coins} /><span className="strong">{baht(customCalc.baht)}</span></> : <span className="strong">—</span>}</span>
            </div>
          ) : null}
        </div>
      )}

      <h3 className="mt-24 mb-8">ช่องทางชำระเงิน</h3>
      <div className="stack" style={{ gap: 8 }}>
        <div className="pick is-active" aria-current="true"><ChatIcon name="qr" size={22} /><span className="grow"><span className="strong">QR พร้อมเพย์ / TrueMoney</span><span className="muted" style={{ display: "block", fontSize: 13 }}>สแกนจ่ายผ่านแอปธนาคารหรือ TrueMoney · คอยน์เข้าอัตโนมัติ ภายใน 5-10 นาที</span></span></div>
        <div className="pick" aria-disabled="true" style={{ opacity: 0.6, cursor: "not-allowed" }}><ChatIcon name="card" size={22} /><span className="grow"><span className="strong">บัตรเครดิต / เดบิต</span></span><Chip size="sm">{UNAVAILABLE}</Chip></div>
      </div>

      <div className="pay-bar">
        <div className="grow muted" style={{ fontSize: 13 }} data-testid="pay-summary">{sel ? <>ได้รับ <b className="coin" style={{ display: "inline-flex" }}><i className="coin__dot" />{fmtCoins(sel.tokens)}</b> คอยน์ · {fmtCoins(sel.clubPoint)} คุยพ้อยท์</> : "เลือกแพ็กเกจที่ต้องการ"}</div>
        <Button variant="primary" size="lg" disabled={!sel} onClick={() => setPayFor(sel)}>{sel ? `ชำระเงิน ${baht(sel.price)}` : "เลือกแพ็กเกจ"}</Button>
      </div>
      <p className="faint mt-8" style={{ fontSize: 12 }}>เติมแล้วได้คุยพ้อยท์สะสมระดับ <Link to="/club">คุยคลับ</Link> · มีปัญหาการเติมเงิน แจ้งทีมงานพร้อมเลข Ref ที่แสดงตอนชำระ</p>

      </>)}

      <PendingTopups version={version} onConfirmed={applyResult} />

      <h2 className="mt-24">ประวัติคอยน์</h2>
      <Card className="mt-16"><CoinHistory refreshKey={`${version}:${coins}`} /></Card>

      <PaymentQrModal open={!!payFor} pkg={payFor} onClose={() => { setPayFor(null); setVersion((v) => v + 1); }} onConfirm={onConfirm} />
    </div>
  );
}
